import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  DialogTitle,
  DialogActions,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Download, RotateCcw } from "lucide-react";
import {
  fetchCagUploadHistory,
  rollbackCagUploadVersion,
  downloadCagUploadFile,
  downloadCagUploadVersion,
} from "../../api/cagConfigurations";
import { LoadingWaveDots } from "../common/Loader/LoadingWave";

const formatVersionTitle = (ver) => {
  if (!ver) return "Selected Version";
  if (typeof ver === "string" && /^version[-_ ]?\d+$/i.test(ver.trim())) {
    const num = ver.trim().replace(/^version[-_ ]?/i, "");
    return `Version ${num}`;
  }
  return ver;
};

const getInitials = (name) => {
  if (!name) return "U";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const formatUploadTime = (isoString) => {
  if (!isoString) return "";
  try {
    // Backend timestamps are UTC without a zone suffix (same as formatDateTime)
    const trimmed = String(isoString).replace(/(\.\d{3})\d+/, "$1");
    const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
    const date = new Date(hasZone ? trimmed : `${trimmed}Z`);
    if (Number.isNaN(date.getTime())) return isoString;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    if (diffMs < 0) return "Just now";
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) {
      const diffMins = Math.max(1, Math.floor(diffMs / (1000 * 60)));
      return `Uploaded ${diffMins}m ago`;
    }
    if (diffHours < 24) {
      return `Uploaded ${diffHours}h ago`;
    }
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return `Uploaded 1 day ago`;
    if (diffDays < 7) return `Uploaded ${diffDays} days ago`;
    return `Uploaded ${date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}`;
  } catch {
    return isoString;
  }
};

const getBadge = (isCurrent, isSystem) => {
  if (isCurrent) return "Current";
  if (isSystem) return "System";
  return null;
};

const normalizeUploadList = (dataList) => {
  if (!Array.isArray(dataList)) return [];
  return dataList.map((raw, index) => {
    const isSystem = !raw.fileName || raw.version === "version-1";
    const isCurrent = index === 0;
    return {
      id: raw.id || raw.version || index,
      version: raw.version || `version-${dataList.length - index}`,
      fileName:
        raw.fileName ||
        (isSystem ? "Initial data — system backfill" : "cag-config.xlsx"),
      uploadedFileBlobUrl: raw.uploadedFileBlobUrl,
      uploadedBy:
        raw.createdByName ||
        raw.uploadedBy ||
        (isSystem ? "System" : "Unknown User"),
      uploadedByEmail: raw.createdByEmail || raw.uploadedByEmail || "",
      uploadedAt: raw.createdAt
        ? formatUploadTime(raw.createdAt)
        : raw.uploadedAt || "",
      recordCount: raw.uploadedCount ?? raw.recordCount ?? 0,
      uploadedCount: raw.uploadedCount ?? raw.recordCount ?? 0,
      publishedCount: raw.publishedCount ?? 0,
      validCount: raw.validCount ?? 0,
      invalidCount: raw.invalidCount ?? 0,
      isCurrent,
      badge: getBadge(isCurrent, isSystem),
      isSystem,
      raw,
    };
  });
};

const CagUploadHistoryModal = ({ open, onClose, onResetSuccess }) => {
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetConfirmItem, setResetConfirmItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [downloadingVersionId, setDownloadingVersionId] = useState(null);
  const [downloadingFileId, setDownloadingFileId] = useState(null);

  // Load history from API
  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await fetchCagUploadHistory();
      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.content || res.data?.history || res.data?.data;
      if (Array.isArray(data) && data.length > 0) {
        setHistoryList(normalizeUploadList(data));
      } else {
        setHistoryList([]);
      }
    } catch (err) {
      console.error("Failed to fetch upload history:", err);
      setHistoryList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadHistory().catch((err) => console.error(err));
    }
  }, [open]);

  // Handler: Download specific version blob file
  const handleDownload = async (item) => {
    if (!item.uploadedFileBlobUrl) return;
    const itemId = item.version || item.id;
    setDownloadingFileId(itemId);
    try {
      await downloadCagUploadFile(
        item.uploadedFileBlobUrl,
        item.fileName || `${item.version}.xlsx`,
      );
    } catch (err) {
      console.error("Failed to download version file via API:", err);
    } finally {
      setDownloadingFileId(null);
    }
  };

  // Handler: Download version file via GET /api/cag/uploads/{version}/download
  const handleVersionDownload = async (item) => {
    const version = item.version || item.id;
    if (!version) return;
    const itemId = item.version || item.id;
    setDownloadingVersionId(itemId);
    try {
      await downloadCagUploadVersion(
        version,
        item.fileName || `${version}.xlsx`,
      );
    } catch (err) {
      console.error("Failed to download version file via API:", err);
    } finally {
      setDownloadingVersionId(null);
    }
  };

  // Handler: Confirm Reset / Rollback
  const handleConfirmReset = async () => {
    if (!resetConfirmItem) return;
    setActionLoading(true);
    try {
      const version = resetConfirmItem.version || resetConfirmItem.id;
      await rollbackCagUploadVersion(version);
      setResetConfirmItem(null);
      if (onResetSuccess) {
        onResetSuccess(resetConfirmItem);
      }
      onClose();
    } catch (err) {
      console.error("Failed to rollback to version:", err);
      setResetConfirmItem(null);
      if (onResetSuccess) {
        onResetSuccess(resetConfirmItem);
      }
      onClose();
    } finally {
      setActionLoading(false);
    }
  };

  // Computed summary
  const currentItem = historyList.find((h) => h.isCurrent) || historyList[0];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "95vw", md: "860px" },
              maxHeight: "90vh",
              borderRadius: "16px",
              backgroundColor: "#F7F8FB",
              p: { xs: "16px", md: "24px" },
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            },
          },
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "24px",
                lineHeight: "32px",
                color: "#1E293B",
              }}
            >
              Version History
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#64748B",
                mt: 0.5,
              }}
            >
              {historyList.length}{" "}
              {historyList.length === 1 ? "version" : "versions"}
              {currentItem ? (
                <>
                  {" "}
                  · current is{" "}
                  <strong style={{ color: "#334155" }}>
                    {currentItem?.version || "version-1"}
                  </strong>
                </>
              ) : null}
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="Close dialog"
            sx={{
              color: "#94A3B8",
              "&:hover": {
                color: "#1E293B",
                bgcolor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content Section: Timeline */}
        <DialogContent
          sx={{
            p: { xs: 1.5, md: 3 },
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            maxHeight: "65vh",
            overflowY: "auto",
          }}
        >
          {loading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                gap: 1.5,
              }}
            >
              <CircularProgress size={32} sx={{ color: "#403C6F" }} />
              <Typography sx={{ color: "#64748B", fontSize: "14px" }}>
                Loading version history...
              </Typography>
            </Box>
          )}
          {!loading && historyList.length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                color: "#64748B",
              }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                No version history found.
              </Typography>
            </Box>
          )}
          {!loading && historyList.length > 0 && (
            <Box sx={{ position: "relative", pl: { xs: 2, md: 3 }, py: 1 }}>
              {/* Vertical timeline line */}
              <Box
                sx={{
                  position: "absolute",
                  left: { xs: "23px", md: "31px" },
                  top: 24,
                  bottom: 24,
                  width: "2px",
                  bgcolor: "#E2E8F0",
                  zIndex: 1,
                }}
              />

              {historyList.map((item, index) => {
                const isCurrent = Boolean(
                  item.isCurrent || item.badge === "Current",
                );
                const isLast = index === historyList.length - 1;
                const itemId = item.version || item.id;
                const isDownloadingVersion = downloadingVersionId === itemId;
                const isDownloadingFile = downloadingFileId === itemId;

                return (
                  <Box
                    key={item.id || item.version || index}
                    sx={{
                      position: "relative",
                      display: "flex",
                      gap: { xs: 2, md: 3 },
                      pb: isLast ? 1 : 4,
                      zIndex: 2,
                    }}
                  >
                    {/* Timeline Node */}
                    <Box
                      sx={{
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        bgcolor: isCurrent ? "#403C6F" : "#FFFFFF",
                        border: isCurrent
                          ? "3px solid #DDDCE8"
                          : "2px solid #CBD5E1",
                        boxShadow: isCurrent
                          ? "0 0 0 3px rgba(64, 60, 111, 0.2)"
                          : "none",
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />

                    {/* Version Details Card */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Row 1: Version Title + Badges + Actions */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 0.75,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "16px",
                              color: "#0F172A",
                              lineHeight: "22px",
                            }}
                          >
                            {item.version}
                          </Typography>

                          {isCurrent && (
                            <Chip
                              label="Current"
                              size="small"
                              sx={{
                                height: "22px",
                                fontSize: "12px",
                                fontWeight: 600,
                                bgcolor: "#EDEBF7",
                                color: "#403C6F",
                                border: "1px solid #D1CFE5",
                                borderRadius: "12px",
                              }}
                            />
                          )}

                          {item.badge === "System" && (
                            <Chip
                              label="System"
                              size="small"
                              sx={{
                                height: "22px",
                                fontSize: "12px",
                                fontWeight: 600,
                                bgcolor: "#F1F5F9",
                                color: "#475569",
                                border: "1px solid #E2E8F0",
                                borderRadius: "12px",
                              }}
                            />
                          )}
                        </Box>

                        {/* Action Buttons */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {!item.isSystem && (
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={isDownloadingVersion}
                              onClick={() =>
                                handleVersionDownload(item).catch((err) =>
                                  console.error(err),
                                )
                              }
                              startIcon={
                                isDownloadingVersion ? (
                                  <CircularProgress
                                    size={14}
                                    sx={{ color: "inherit" }}
                                  />
                                ) : (
                                  <Download size={14} />
                                )
                              }
                              sx={{
                                height: "30px",
                                borderRadius: "8px",
                                border: "1px solid #D0D5DD",
                                color: "#344054",
                                bgcolor: "#FFFFFF",
                                textTransform: "none",
                                fontSize: "13px",
                                fontWeight: 500,
                                px: 1.5,
                                boxShadow: "none",
                                "&:hover": {
                                  borderColor: "#98A2B3",
                                  bgcolor: "#F8FAFC",
                                },
                                "&.Mui-disabled": {
                                  bgcolor: "#F8FAFC",
                                  color: "#98A2B3",
                                  borderColor: "#EAECF0",
                                },
                              }}
                            >
                              {isDownloadingVersion
                                ? "Downloading..."
                                : "Download"}
                            </Button>
                          )}

                          {!isCurrent && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setResetConfirmItem(item)}
                              startIcon={<RotateCcw size={14} />}
                              sx={{
                                height: "30px",
                                borderRadius: "8px",
                                border: "1px solid #FDA29B",
                                color: "#B42318",
                                bgcolor: "#FFFFFF",
                                textTransform: "none",
                                fontSize: "13px",
                                fontWeight: 500,
                                px: 1.5,
                                boxShadow: "none",
                                "&:hover": {
                                  borderColor: "#F04438",
                                  bgcolor: "#FEF3F2",
                                },
                              }}
                            >
                              Reset
                            </Button>
                          )}
                        </Box>
                      </Box>

                      {/* Row 2: File Name + Download Icon */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          mb: 0.75,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: item.isSystem ? 400 : 500,
                            fontStyle: item.isSystem ? "italic" : "normal",
                            color: item.isSystem ? "#64748B" : "#1E293B",
                          }}
                        >
                          {item.fileName}
                        </Typography>

                        {!item.isSystem && (
                          <Tooltip
                            title={
                              isDownloadingFile
                                ? "Downloading..."
                                : "Download uploaded file"
                            }
                            arrow
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={isDownloadingFile}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(item).catch((err) =>
                                    console.error(err),
                                  );
                                }}
                                aria-label="Download uploaded file records"
                                sx={{
                                  p: isDownloadingFile ? "4px 8px" : "2px",
                                  minWidth: isDownloadingFile ? "28px" : "auto",
                                  height: "24px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#403C6F",
                                  borderRadius: "6px",
                                  border: "1px solid #E2E8F0",
                                  bgcolor: "#FFFFFF",
                                  "&:hover": {
                                    bgcolor: "#F8FAFC",
                                    borderColor: "#CBD5E1",
                                    color: "#332F5B",
                                  },
                                  "&.Mui-disabled": {
                                    bgcolor: "#F8FAFC",
                                    borderColor: "#E2E8F0",
                                  },
                                }}
                              >
                                {isDownloadingFile ? (
                                  <LoadingWaveDots label="Downloading file" />
                                ) : (
                                  <Download size={13} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>

                      {/* Row 3: User Avatar, Name, Timestamp */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 1.25,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "11px",
                            fontWeight: 700,
                            bgcolor: "#403C6F",
                            color: "#FFFFFF",
                          }}
                        >
                          {getInitials(item.uploadedBy)}
                        </Avatar>

                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#1E293B",
                          }}
                        >
                          {item.uploadedBy}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#64748B",
                          }}
                        >
                          {item.uploadedAt}
                        </Typography>
                      </Box>

                      {/* Row 4: Uploaded Count & Published Count */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: { xs: 2, sm: 3 },
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.75,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "13px",
                              color: "#64748B",
                            }}
                          >
                            Uploaded Records:
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "15px",
                              color: "#0F172A",
                              lineHeight: "20px",
                            }}
                          >
                            {Number(
                              item.uploadedCount ?? item.recordCount ?? 0,
                            ).toLocaleString()}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.75,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "13px",
                              color: "#64748B",
                            }}
                          >
                            Published Records:
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "15px",
                              color: "#0F172A",
                              lineHeight: "20px",
                            }}
                          >
                            {Number(item.publishedCount ?? 0).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Reset */}
      <Dialog
        open={Boolean(resetConfirmItem)}
        onClose={() => setResetConfirmItem(null)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "16px",
              p: { xs: "20px", sm: "28px" },
              width: "440px",
              maxWidth: "92vw",
              backgroundColor: "#FFFFFF",
              position: "relative",
              boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
            },
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={() => setResetConfirmItem(null)}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "#6E6D92",
            "&:hover": {
              color: "#403C6F",
              bgcolor: "rgba(64, 60, 111, 0.04)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            backgroundColor: "#F4EFFE",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <RotateCcw size={26} color="#403C6F" />
        </Box>

        <DialogTitle
          sx={{
            p: 0,
            fontSize: "20px",
            fontWeight: 700,
            color: "#403C6F",
            textAlign: "center",
            mb: 1,
            lineHeight: "28px",
          }}
        >
          Reset CAG Configuration to{" "}
          {formatVersionTitle(resetConfirmItem?.version)}?
        </DialogTitle>

        <DialogContent sx={{ p: 0, mb: 2.5 }}>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#6E6D92",
              lineHeight: "22px",
              textAlign: "center",
              mb: 2,
            }}
          >
            Resetting will return the CAG configuration to the state it was in
            when {formatVersionTitle(resetConfirmItem?.version)} was published.
            Any changes introduced in versions published after{" "}
            {formatVersionTitle(resetConfirmItem?.version)} will be permanently
            removed.
          </Typography>

          {/* 30-minute Warning Message */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: "10px 14px",
              bgcolor: "#FFFBEB",
              border: "1px solid #FEDF89",
              borderRadius: "8px",
              color: "#B54708",
              fontSize: "13px",
              fontWeight: 500,
              mb: 2,
            }}
          >
            <span style={{ fontSize: "15px" }}>⚠️</span>
            <Typography
              sx={{ fontSize: "13px", color: "#B54708", fontWeight: 500 }}
            >
              CAG configuration changes may take up to 30 minutes to take
              effect.
            </Typography>
          </Box>

          {/* Staging Version Details Card */}
          <Box
            sx={{
              backgroundColor: "#F7F8FB",
              borderRadius: "12px",
              border: "1px solid #E8E8E9",
              p: "12px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{ fontSize: "13px", color: "#6E6D92", fontWeight: 500 }}
              >
                Target Version:
              </Typography>
              <Typography
                sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 700 }}
              >
                {formatVersionTitle(resetConfirmItem?.version)}
              </Typography>
            </Box>

            {resetConfirmItem?.fileName && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{ fontSize: "13px", color: "#6E6D92", fontWeight: 500 }}
                >
                  File Name:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#1B2F3F",
                    fontWeight: 600,
                    maxWidth: "200px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={resetConfirmItem?.fileName}
                >
                  {resetConfirmItem?.fileName}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{ fontSize: "13px", color: "#6E6D92", fontWeight: 500 }}
              >
                Uploaded Records:
              </Typography>
              <Typography
                sx={{ fontSize: "13px", color: "#1B2F3F", fontWeight: 600 }}
              >
                {Number(
                  resetConfirmItem?.uploadedCount ??
                    resetConfirmItem?.recordCount ??
                    0,
                ).toLocaleString()}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{ fontSize: "13px", color: "#6E6D92", fontWeight: 500 }}
              >
                Published Records:
              </Typography>
              <Typography
                sx={{ fontSize: "13px", color: "#1B2F3F", fontWeight: 600 }}
              >
                {Number(resetConfirmItem?.publishedCount ?? 0).toLocaleString()}
              </Typography>
            </Box>

            {resetConfirmItem?.uploadedBy && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{ fontSize: "13px", color: "#6E6D92", fontWeight: 500 }}
                >
                  Uploaded By:
                </Typography>
                <Typography
                  sx={{ fontSize: "13px", color: "#1B2F3F", fontWeight: 500 }}
                >
                  {resetConfirmItem?.uploadedBy}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{ p: 0, display: "flex", justifyContent: "center", gap: 1.5 }}
        >
          <Button
            variant="outlined"
            onClick={() => setResetConfirmItem(null)}
            sx={{
              flex: 1,
              height: "40px",
              borderRadius: "48px",
              borderColor: "#E8E8E9",
              color: "#6E6D92",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              "&:hover": {
                borderColor: "#403C6F",
                color: "#403C6F",
                backgroundColor: "rgba(64, 60, 111, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmReset}
            disabled={actionLoading}
            sx={{
              flex: 1,
              height: "40px",
              borderRadius: "48px",
              backgroundColor: "#D92D20",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#B42318",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                bgcolor: "rgba(217, 45, 32, 0.4)",
                color: "rgba(255, 255, 255, 0.7)",
              },
              "&:focus-visible": {
                outline: "2px solid #D92D20",
                outlineOffset: "2px",
              },
            }}
          >
            {actionLoading ? "Resetting..." : "Reset Configuration"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

CagUploadHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onResetSuccess: PropTypes.func,
};

export default CagUploadHistoryModal;
